import { useRouter } from 'next/router';
import dynamic from 'next/dynamic';
import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import ivclass from '@invideoio/web-shared/Modules/IVClass';
import Masonry from 'react-masonry-css';
import qs from 'query-string';

import { isUserLoggedIn } from '@utils/common.utils';
import redirectToEditor, {
  getEditorRedirectionUrl,
} from '@utils/template-category/redirect-to-editor';
import { RedirectContext } from '@store/store';
import { AUTH_TYPE, baseHost } from '@constants/homepage';
import {
  TemplateSearchQuery,
  LIHPTemplate,
  TemplateDimension,
} from '@invideoio/web-shared/types/Templates';
import { MASONRY_BREAKPOINTS } from '@constants/templates';

import useLazyload from '@hooks/useLazyload';
import TemplateTile from './TemplateTile';
import {
  CTAInvideoAiPromptPage,
  CTAVideoWallTemplateUrl,
} from '@constants/makepage';
import { VideoWallTemplateBasic } from '@components/templates/types';

const TemplatesPreview = dynamic(
  () =>
    /* webpackChunkName: "TemplatesPreview" */
    import('@invideoio/web-shared/Modules/IVTemplatePreview'),
);

const VideoWallTemplatesPreview = dynamic(
  () =>
    /* webpackChunkName: "VideoWallTemplatesPreview" */
    import(
      '@components/video-wall-category-templates/VideoWallTemplatePreview'
    ),
);

const VideoWallTemplatePreviewHeader = dynamic(
  () =>
    /* webpackChunkName: "TemplatePreviewHeader" */
    import(
      '@components/video-wall-category-templates/VideoWallTemplatePreviewHeader'
    ),
);

const BackToTop = dynamic(
  () =>
    /* webpackChunkName: "BackToTop" */
    import('@components/BackToTop'),
);
interface CategoryTemplatesProps {
  templateList: any;
  filters: TemplateSearchQuery;
  totalCount: number;
  currerntPage?: number;
  platform: string;
  moreLink?: string;
}

function VideoWallCategoryTemplates(props: CategoryTemplatesProps) {
  const { templateList, platform, moreLink } = props;
  const router = useRouter();

  const [selectedTemplate, setSelectedTemplate] = useState<LIHPTemplate>();

  const [isBackToTopVisible, setIsBackToTopVisible] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const { handleClick } = useContext(RedirectContext);

  const scrollToTop = () => {
    containerRef?.current?.scrollTo({ top: 0, behavior: 'smooth' });
  };

  useLazyload();
  const componentRef = useRef<HTMLDivElement | null>(null);
  const [componentDimension, setComponentDimension] = useState<{
    width: number;
    height: number;
  } | null>(null);
  useEffect(() => {
    const updateDimension = () => {
      if (!componentRef.current) return;

      const { width, height } = componentRef.current.getBoundingClientRect();

      const hasChanged =
        width !== componentDimension?.width ||
        height !== componentDimension?.height;

      if (hasChanged) {
        setComponentDimension({ width, height });
      }
    };

    updateDimension();

    let timeOut: ReturnType<typeof setTimeout> | null;
    const resizeListener = () => {
      if (timeOut) clearTimeout(timeOut);
      timeOut = setTimeout(updateDimension, 1000);
    };

    window.addEventListener('resize', resizeListener);
    return () => {
      window.removeEventListener('resize', resizeListener);
    };
  }, [componentDimension]);

  const handleScroll = () => {
    if (
      containerRef?.current?.scrollTop &&
      containerRef?.current?.scrollTop > 2000
    ) {
      setIsBackToTopVisible(true);
    } else {
      setIsBackToTopVisible(false);
    }
  };

  const [templateState, setTemplateState] = useState({
    hasNoMore: false,
    isFetching: false,
  });

  function openEditor(selectedDimension: any) {
    if (selectedTemplate) {
      redirectToEditor(
        '',
        selectedDimension,
        selectedTemplate.id.toString(),
        true,
        'all',
        'story',
        'oneClick',
        true,
      );
    }
  }

  const openTemplatePreview = async (template: LIHPTemplate) => {
    onSelectTemplate(template);
  };

  const onUseTemplate = (template: any, selectedDimension: any) => {
    if (!isUserLoggedIn()) {
      const url = getEditorRedirectionUrl(
        '',
        selectedDimension,
        template.id.toString(),
        true,
        'all',
        'story',
        'oneClick',
        true,
      );

      handleClick(baseHost + url, { tab: AUTH_TYPE.SIGNUP });
    } else {
      openEditor(selectedDimension);
    }
  };

  const selectTemplate = useCallback(
    (template_id: number, show_preview: string) => {
      if (template_id && show_preview === 'true' && templateList?.length) {
        const template = templateList.find(
          (t: VideoWallTemplateBasic) => t.id === Number(template_id),
        );
        if (template) {
          // Convert to LIHPTemplate format before setting
          const lihpTemplate: LIHPTemplate = {
            ...template,
            dimensions: ['16:9'],
            isFavouriteTemplate: false,
            template_type: '',
            creator_id: 0,
            creator_name: '',
            workflow: '',
            categories: [],
            industries: [],
            templateURL: CTAInvideoAiPromptPage,
          };
          setSelectedTemplate(lihpTemplate);
        }
      }
    },
    [templateList],
  );

  useEffect(() => {
    if (router.query) {
      const isCMSPreview = router.query['cmsPreview'];

      if (isCMSPreview && isCMSPreview === 'true') {
        setTemplateState(currentState => ({
          ...currentState,
          hasNoMore: true,
        }));
      }
    }
  }, [router]);

  useEffect(() => {
    const { template_id, show_preview } = router.query;
    selectTemplate(Number(template_id), show_preview as string);
  }, [router.query, selectTemplate]);

  useEffect(() => {
    setTemplateState(currentState => ({
      ...currentState,
      templates: templateList,
    }));
  }, [templateList]);

  const handleRouteChange = useCallback(() => {
    const parsed = qs.parse(window.location.search);
    const template_id = parsed.template_id;
    const show_preview = parsed.show_preview;

    if (!template_id || show_preview !== 'true') {
      setSelectedTemplate(undefined);
      return;
    }

    if (templateList?.length) {
      const template = templateList.find(
        (t: VideoWallTemplateBasic) => t.id === Number(template_id),
      );
      if (template) {
        const lihpTemplate: LIHPTemplate = {
          ...template,
          dimensions: ['16:9'],
          isFavouriteTemplate: false,
          template_type: '',
          creator_id: 0,
          creator_name: '',
          workflow: '',
          categories: [],
          industries: [],
          templateURL: CTAInvideoAiPromptPage,
        };
        setSelectedTemplate(lihpTemplate);
      }
    }
  }, [templateList]);

  useEffect(() => {
    // Listen to route changes

    // Listen to both route changes and popstate events
    router.events.on('routeChangeComplete', handleRouteChange);
    window.addEventListener('popstate', handleRouteChange);

    // Handle initial load
    handleRouteChange();

    return () => {
      router.events.off('routeChangeComplete', handleRouteChange);
      window.removeEventListener('popstate', handleRouteChange);
    };
  }, [router, templateList, handleRouteChange]);

  const closeModal = () => {
    const parsed = qs.parse(window.location.search);
    const { template_id, show_preview, ...restQuery } = parsed;
    const newQuery = qs.stringify(restQuery);
    router.push(
      `${window.location.pathname}${newQuery ? `?${newQuery}` : ''}`,
      undefined,
      { shallow: true },
    );
  };
  const templatePreviewModalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const close = (e: KeyboardEvent) => {
      // Escape keyCode is 27
      // Close modal on esc key press
      if (e.key === 'Escape') {
        closeModal();
      }
    };
    window.addEventListener('keydown', close);
    return () => window.removeEventListener('keydown', close);
  }, []);

  const onSelectTemplate = (template?: LIHPTemplate) => {
    if (template) {
      const url = new URL(window.location.href);
      url.searchParams.set('template_id', template.id.toString());
      url.searchParams.set('show_preview', 'true');
      window.history.pushState({}, '', url);

      selectTemplate(template.id, 'true');
    }
  };

  return (
    <div
      className={ivclass(
        'iv-w-full',
        'lg:iv-py-25 xl:iv-py-25 xl:iv-pl-25 lg:iv-pl-25 md:iv-py-15 md:iv-px-5 sm:iv-px-5 sm:iv-py-15',
      )}
      ref={containerRef}
      onScroll={handleScroll}
    >
      <div className={ivclass('')}>
        <div className={ivclass('iv-flex iv-h-full iv-overflow-hidden')}>
          <div
            ref={containerRef}
            onScroll={handleScroll}
            style={{ width: `calc(100% )` }}
            className="iv-h-full iv-overflow-auto iv-relative"
          >
            <div className={ivclass('iv-px-[0px]')}>
              <Masonry
                breakpointCols={MASONRY_BREAKPOINTS}
                className="lihp-template-grid"
                columnClassName="lihp-template-grid_column"
              >
                {templateList.map((template: VideoWallTemplateBasic) => (
                  <TemplateTile
                    onTemplateSelect={onSelectTemplate}
                    workflow={'marketing'}
                    key={template.id}
                    template={template}
                  />
                ))}
              </Masonry>
            </div>
          </div>
        </div>
      </div>

      <div
        className={ivclass(
          'iv-fixed  iv-left-0 iv-right-0 iv-bottom-0 iv-z-[9999]',
          'iv-overflow-auto',
          'iv-bg-grey-0',
          'iv-ease-in-out iv-duration-500',
          selectedTemplate
            ? 'iv-opacity-1 iv-visible iv-top-[56px]'
            : 'iv-opacity-0 iv-invisible iv-top-[56px]',
          '!iv-border-t-[1px] iv-border-grey-7 iv-border-solid iv-border-x-0 iv-border-y-0',
          !selectedTemplate && '!iv-border-t-[0px]',
        )}
        ref={templatePreviewModalRef}
      >
        {selectedTemplate && (
          <div className="iv-px-20">
            <VideoWallTemplatePreviewHeader
              template={selectedTemplate}
              isModal={true}
              closeModal={closeModal}
            />
          </div>
        )}
        {selectedTemplate && (
          <div className={ivclass('iv-flex iv-justify-center')}>
            <VideoWallTemplatesPreview
              templatesList={templateList}
              previewData={selectedTemplate}
              options={{
                showRelatedTemplates: true,
                wrapperClass: '!iv-w-full',
              }}
            />
          </div>
        )}
      </div>

      {isBackToTopVisible && <BackToTop onClick={scrollToTop} />}
    </div>
  );
}

export default VideoWallCategoryTemplates;
